<script setup>
import { onMounted, onUnmounted, ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import KanbanHeader from './KanbanHeader.vue';
import ContactAPI from '../../../../api/contacts';
import KanbanAPI from '../../../../api/kanban';

const props = defineProps({
  currentView: {
    type: String,
    default: 'map',
  },
});

const emit = defineEmits(['switchView']);
const store = useStore();
const route = useRoute();
const router = useRouter();

const activeFilters = ref(null);
const mapContainerRef = ref(null);
const mapInstance = ref(null);
const markersLayer = ref(null);
const userLocationMarker = ref(null);
const routeLayer = ref(null);
const userCurrentPosition = ref(null);
const locationItems = ref([]);
const isLoadingLocationItems = ref(false);
const geocodedPoints = ref([]);
const markerByItemId = ref(new Map());
const lastLoadedFunnelId = ref(null);
const selectedFunnel = computed(
  () => store.getters['funnel/getSelectedFunnel']
);

const LEAFLET_CSS_ID = 'leaflet-css-cdn';
const LEAFLET_JS_ID = 'leaflet-js-cdn';
const LEAFLET_CSS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const DEFAULT_MAP_CENTER = { lat: -23.55052, lon: -46.633308, zoom: 11 };
const USER_LOCATION_ZOOM = 13;

const loadLeafletAssets = async () => {
  if (!document.getElementById(LEAFLET_CSS_ID)) {
    const link = document.createElement('link');
    link.id = LEAFLET_CSS_ID;
    link.rel = 'stylesheet';
    link.href = LEAFLET_CSS_URL;
    document.head.appendChild(link);
  }

  if (window.L) return;

  await new Promise((resolve, reject) => {
    const existingScript = document.getElementById(LEAFLET_JS_ID);
    if (existingScript) {
      existingScript.addEventListener('load', resolve, { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Falha ao carregar Leaflet JS')),
        { once: true }
      );
      return;
    }

    const script = document.createElement('script');
    script.id = LEAFLET_JS_ID;
    script.src = LEAFLET_JS_URL;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error('Falha ao carregar Leaflet JS'));
    document.body.appendChild(script);
  });
};

const getUserCurrentPosition = () =>
  new Promise(resolve => {
    if (!navigator?.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        resolve({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
        });
      },
      () => resolve(null),
      {
        enableHighAccuracy: true,
        timeout: 6000,
        maximumAge: 5 * 60 * 1000,
      }
    );
  });

const navigateToItemDetails = itemId => {
  router.push({
    query: {
      ...route.query,
      item: itemId,
    },
  });
};

const extractItemsFromResponse = response => {
  if (!response?.data) return [];
  if (Array.isArray(response.data.items)) return response.data.items;
  if (Array.isArray(response.data)) return response.data;
  return [];
};

const geocodeItemLocation = async item => {
  const locationCep = item?.item_details?.location_cep;
  const locationAddress = item?.item_details?.location_address;
  const queryCandidates = [];
  if (locationAddress) queryCandidates.push(locationAddress);
  if (locationCep) queryCandidates.push(`${locationCep}, Brasil`);
  if (!queryCandidates.length) return null;

  try {
    let foundResult = null;
    for (const query of queryCandidates) {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`
      );
      const data = await response.json();
      if (response.ok && Array.isArray(data) && data.length) {
        foundResult = data[0];
        break;
      }
    }

    if (!foundResult) return null;

    return {
      itemId: item?.id,
      itemPriority: item?.item_details?.priority || '',
      lat: Number(foundResult.lat),
      lon: Number(foundResult.lon),
      label:
        item?.item_details?.title ||
        item?.title ||
        `Item #${item?.id || ''}`.trim(),
      locationAddress,
      locationCep,
    };
  } catch (error) {
    return null;
  }
};

const buildItemPopupHtml = point => {
  const priority = (point.itemPriority || '').toLowerCase();
  const showPriority = priority && priority !== 'none';
  const priorityLabel = showPriority ? priority.toUpperCase() : '';

  return `
    <div style="min-width:220px;max-width:280px;padding:2px 0;">
      <div style="font-size:14px;font-weight:600;color:#0f172a;line-height:1.3;">
        ${point.label}
        <span style="font-size:12px;font-weight:400;color:#64748b;"> (ID: ${point.itemId})</span>
      </div>
      ${
        showPriority
          ? `<div style="margin-top:6px;">
              <span style="display:inline-block;font-size:10px;font-weight:600;padding:2px 8px;border-radius:999px;background:#e2e8f0;color:#334155;">
                ${priorityLabel}
              </span>
            </div>`
          : ''
      }
      <div style="margin-top:8px;font-size:12px;color:#334155;line-height:1.4;">
        <div><strong>Endereco:</strong> ${point.locationAddress || '-'}</div>
        <div><strong>CEP:</strong> ${point.locationCep || '-'}</div>
      </div>
      <button
        type="button"
        data-item-id="${point.itemId}"
        class="map-open-item-details"
        style="margin-top:10px;font-size:12px;font-weight:600;color:#ffffff;background:#3b82f6;border:none;border-radius:6px;padding:6px 10px;cursor:pointer;"
      >
        Ver detalhes
      </button>
    </div>
  `;
};

const bindPopupDetailsAction = marker => {
  marker.on('popupopen', event => {
    const popupElement = event.popup?.getElement?.();
    const detailsButton = popupElement?.querySelector('.map-open-item-details');
    if (!detailsButton || detailsButton.dataset.bound === 'true') return;

    detailsButton.dataset.bound = 'true';
    detailsButton.addEventListener('click', clickEvent => {
      clickEvent.preventDefault();
      clickEvent.stopPropagation();
      const itemId = detailsButton.getAttribute('data-item-id');
      if (itemId) {
        navigateToItemDetails(itemId);
      }
    });
  });
};

const renderUserLocationMarker = position => {
  if (!mapInstance.value || !window.L || !position) return;

  if (userLocationMarker.value) {
    try {
      mapInstance.value.removeLayer(userLocationMarker.value);
    } catch (e) {
      // ignore
    }
  }

  userLocationMarker.value = window.L.circleMarker([position.lat, position.lon], {
    radius: 8,
    fillColor: '#3b82f6',
    color: '#ffffff',
    weight: 2,
    opacity: 1,
    fillOpacity: 0.95,
  })
    .bindPopup('Voce esta aqui')
    .addTo(mapInstance.value);
};

const clearRouteLayer = () => {
  if (!routeLayer.value || !mapInstance.value) return;

  try {
    mapInstance.value.removeLayer(routeLayer.value);
  } catch (e) {
    // ignore
  }
  routeLayer.value = null;
};

const clearLocationMarkers = () => {
  clearRouteLayer();

  if (markersLayer.value && mapInstance.value) {
    try {
      mapInstance.value.removeLayer(markersLayer.value);
    } catch (e) {
      // ignore
    }
    markersLayer.value = null;
  }
  markerByItemId.value = new Map();
  geocodedPoints.value = [];
  locationItems.value = [];
};

const renderLocationPins = async items => {
  if (!mapInstance.value || !window.L) return;

  clearLocationMarkers();

  const itemsWithLocation = items.filter(
    item => item?.item_details?.location_cep || item?.item_details?.location_address
  );
  locationItems.value = itemsWithLocation;

  if (!itemsWithLocation.length) return;

  const geocodedResults = await Promise.all(
    itemsWithLocation.map(item => geocodeItemLocation(item))
  );
  const validPoints = geocodedResults.filter(
    point => Number.isFinite(point?.lat) && Number.isFinite(point?.lon)
  );
  geocodedPoints.value = validPoints;

  if (!validPoints.length) return;

  markersLayer.value = window.L.layerGroup().addTo(mapInstance.value);

  const bounds = [];
  validPoints.forEach(point => {
    const itemKey = String(point.itemId);
    const marker = window.L.marker([point.lat, point.lon]);
    marker
      .bindPopup(buildItemPopupHtml(point))
      .addTo(markersLayer.value);
    bindPopupDetailsAction(marker);
    markerByItemId.value.set(itemKey, marker);
    bounds.push([point.lat, point.lon]);
  });

  if (bounds.length) {
    mapInstance.value.fitBounds(bounds, { padding: [30, 30] });
  }
};

const focusItemOnMap = async item => {
  if (!mapInstance.value || !window.L || !item) return;
  mapInstance.value.invalidateSize();

  const itemKey = String(item.id);
  let point = geocodedPoints.value.find(p => String(p.itemId) === itemKey);
  let marker = markerByItemId.value.get(itemKey);

  if (!point) {
    point = await geocodeItemLocation(item);
    if (!point) return;
    geocodedPoints.value.push(point);

    if (!markersLayer.value) {
      markersLayer.value = window.L.layerGroup().addTo(mapInstance.value);
    }
    marker = window.L.marker([point.lat, point.lon])
      .bindPopup(buildItemPopupHtml(point))
      .addTo(markersLayer.value);
    bindPopupDetailsAction(marker);
    markerByItemId.value.set(itemKey, marker);
  }

  mapInstance.value.flyTo([point.lat, point.lon], 15, { duration: 0.8 });
  if (marker) {
    setTimeout(() => marker.openPopup(), 250);
  }
};

const openRouteToItem = async item => {
  if (!item || !mapInstance.value || !window.L) return;
  if (!userCurrentPosition.value) return;

  const itemKey = String(item.id);
  let point = geocodedPoints.value.find(p => String(p.itemId) === itemKey);

  if (!point) {
    point = await geocodeItemLocation(item);
  }

  if (!point || !Number.isFinite(point.lat) || !Number.isFinite(point.lon)) return;

  const start = userCurrentPosition.value;
  const routeUrl = `https://router.project-osrm.org/route/v1/driving/${start.lon},${start.lat};${point.lon},${point.lat}?overview=full&geometries=geojson`;

  try {
    const response = await fetch(routeUrl);
    const data = await response.json();
    const routeGeometry = data?.routes?.[0]?.geometry;

    if (!response.ok || !routeGeometry?.coordinates?.length) return;

    clearRouteLayer();
    routeLayer.value = window.L.geoJSON(routeGeometry, {
      style: {
        color: '#2563eb',
        weight: 5,
        opacity: 0.9,
      },
    }).addTo(mapInstance.value);

    mapInstance.value.fitBounds(routeLayer.value.getBounds(), {
      padding: [30, 30],
    });
  } catch (error) {
    console.error('Erro ao carregar rota no mapa:', error);
  }
};

const loadItemsForSelectedFunnel = async () => {
  if (!mapInstance.value || !window.L) return;

  if (!selectedFunnel.value?.id) {
    isLoadingLocationItems.value = false;
    lastLoadedFunnelId.value = null;
    clearLocationMarkers();
    return;
  }

  try {
    isLoadingLocationItems.value = true;
    const funnelId = selectedFunnel.value.id;
    const response = await KanbanAPI.getItems(funnelId);
    const items = extractItemsFromResponse(response);
    await renderLocationPins(items);
    lastLoadedFunnelId.value = funnelId;
  } catch (error) {
    console.error('Erro ao carregar itens do mapa:', error);
    lastLoadedFunnelId.value = null;
    clearLocationMarkers();
  } finally {
    isLoadingLocationItems.value = false;
  }
};

onMounted(async () => {
  try {
    await Promise.all([ContactAPI.get(1), loadLeafletAssets()]);

    if (!mapContainerRef.value || !window.L) return;

    const userPosition = await getUserCurrentPosition();
    userCurrentPosition.value = userPosition;
    const initialCenter = userPosition || DEFAULT_MAP_CENTER;
    const initialZoom = userPosition ? USER_LOCATION_ZOOM : DEFAULT_MAP_CENTER.zoom;

    mapInstance.value = window.L.map(mapContainerRef.value, {
      attributionControl: false,
    }).setView([initialCenter.lat, initialCenter.lon], initialZoom);

    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(mapInstance.value);

    renderUserLocationMarker(userPosition);
    await loadItemsForSelectedFunnel();
  } catch (error) {
    console.error('Erro ao inicializar mapa:', error);
  }
});

watch(
  () => selectedFunnel.value?.id,
  async (newId, oldId) => {
    if (newId === oldId) return;
    if (!mapInstance.value) return;
    if (newId === lastLoadedFunnelId.value) return;
    await loadItemsForSelectedFunnel();
  }
);

onUnmounted(() => {
  clearRouteLayer();

  if (userLocationMarker.value && mapInstance.value) {
    try {
      mapInstance.value.removeLayer(userLocationMarker.value);
    } catch (e) {
      // ignore
    }
    userLocationMarker.value = null;
  }

  if (markersLayer.value) {
    markersLayer.value.clearLayers();
    markersLayer.value = null;
  }
  markerByItemId.value.clear();

  if (mapInstance.value) {
    mapInstance.value.remove();
    mapInstance.value = null;
  }
  userCurrentPosition.value = null;
});
</script>

<template>
  <div class="flex flex-col h-full bg-white dark:bg-slate-900">
    <KanbanHeader
      :current-view="currentView"
      :current-stage="''"
      :search-results="{ total: 0, stages: {} }"
      :columns="[]"
      :active-filters="activeFilters"
      :kanban-items="[]"
      :is-dragging-active="false"
      :dragged-item="null"
      @switchView="(...args) => emit('switchView', ...args)"
    />

    <div class="flex-1 p-4">
      <div class="h-full w-full grid grid-cols-1 md:grid-cols-[1fr_320px] gap-3">
        <div ref="mapContainerRef" class="h-full min-h-[420px] w-full rounded-lg" />
        <aside
          class="h-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 overflow-y-auto"
        >
          <h3 class="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">
            Itens com localizacao
          </h3>

          <div v-if="isLoadingLocationItems" class="space-y-3">
            <div
              v-for="index in 4"
              :key="`location-skeleton-${index}`"
              class="rounded-md border border-slate-200 dark:border-slate-700 px-2 py-2"
            >
              <div class="flex items-start gap-2 animate-pulse">
                <div class="mt-0.5 h-5 w-5 rounded bg-slate-200 dark:bg-slate-700 shrink-0" />
                <div class="min-w-0 flex-1 space-y-2">
                  <div class="h-4 w-3/4 rounded bg-slate-200 dark:bg-slate-700" />
                  <div class="h-3 w-full rounded bg-slate-200 dark:bg-slate-700" />
                  <div class="h-3 w-1/2 rounded bg-slate-200 dark:bg-slate-700" />
                  <div class="h-6 w-24 rounded bg-slate-200 dark:bg-slate-700" />
                </div>
              </div>
            </div>
          </div>

          <p
            v-else-if="!locationItems.length"
            class="text-xs text-slate-500 dark:text-slate-400"
          >
            Nenhum item com localizacao cadastrada.
          </p>

          <div v-else class="space-y-2">
            <button
              v-for="item in locationItems"
              :key="item.id"
              type="button"
              class="w-full text-left rounded-md border border-slate-200 dark:border-slate-700 px-2 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              @click="focusItemOnMap(item)"
            >
              <div class="flex items-start gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-map-icon lucide-map text-slate-500 dark:text-slate-400 shrink-0 mt-0.5"
                >
                  <path d="M14.106 5.553a2 2 0 0 0 1.788 0l3.659-1.83A1 1 0 0 1 21 4.619v12.764a1 1 0 0 1-.553.894l-4.553 2.277a2 2 0 0 1-1.788 0l-4.212-2.106a2 2 0 0 0-1.788 0l-3.659 1.83A1 1 0 0 1 3 19.381V6.618a1 1 0 0 1 .553-.894l4.553-2.277a2 2 0 0 1 1.788 0z" />
                  <path d="M15 5.764v15" />
                  <path d="M9 3.236v15" />
                </svg>
                <div class="min-w-0">
                  <p class="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
                    {{ item?.item_details?.title || item?.title || 'Item' }}
                    <span class="text-slate-500 dark:text-slate-400 font-normal">
                      (ID: {{ item.id }})
                    </span>
                  </p>
                  <p class="text-xs text-slate-600 dark:text-slate-300 truncate">
                    {{ item?.item_details?.location_address || 'Sem endereco' }}
                  </p>
                  <p class="text-xs text-slate-500 dark:text-slate-400 truncate">
                    CEP: {{ item?.item_details?.location_cep || '-' }}
                  </p>
                  <div class="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      class="inline-flex items-center rounded-md px-2 py-1 text-[11px] font-medium transition-colors"
                      style="background-color: #3b82f6; color: #ffffff;"
                      @click.stop="navigateToItemDetails(item.id)"
                    >
                      Ver detalhes
                    </button>
                    <button
                      type="button"
                      class="inline-flex items-center rounded-md px-2 py-1 text-[11px] font-medium transition-colors"
                      style="background-color: #f8fafc; border: 1px solid #cbd5e1; color: #334155;"
                      @click.stop="openRouteToItem(item)"
                    >
                      Ver rota
                    </button>
                  </div>
                </div>
              </div>
            </button>
          </div>
        </aside>
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(.leaflet-container) {
  z-index: 1 !important;
}

:deep(.leaflet-pane),
:deep(.leaflet-control),
:deep(.leaflet-top),
:deep(.leaflet-bottom) {
  z-index: 1 !important;
}
</style>
